import React, { Fragment, useState } from 'react';
import Fuse from 'fuse.js';
import {
  ContainerTypeAmount,
  OrderFulfillmentType,
} from '../../../../graphql/generatedTypes';
import { MultiSelect, Select } from '@mantine/core';
import { FaPlus } from 'react-icons/fa';
import i18n from '@/utils/i18n';
import { Button, Input, Table } from 'reactstrap';
import ContainerTypeDisplay from '../../../../components/ContainerType/ContainerTypeDisplay';
import SelectManualAmountsAmounts from './SelectManualAmountsAmounts';

export type ContainerType = {
  id: number;
  name: string;
  class: string;
  group: string;
  iconUrl: string | null;
  imageUrl: string | null;
  namesByLanguage: Record<string, string> | null;
  resolvedName: string;
  sizeHelper: string | null;
  hexCode: string | null;
  packingUnit: number;
  archived?: boolean;
};

type ContainerTypeRowProps = {
  isDisabled: boolean;
  containerType: ContainerType;
  orderFulfillmentType: OrderFulfillmentType;
  manualAmounts: ContainerTypeAmount[];
  setManualAmounts: (newAmounts: ContainerTypeAmount[]) => void;
  hideAmountCleanColumn?: boolean;
};

const ContainerTypeRow = ({
  containerType,
  orderFulfillmentType,
  manualAmounts,
  setManualAmounts,
  isDisabled,
  hideAmountCleanColumn,
}: ContainerTypeRowProps) => {
  const [amount, setAmount] = useState<string>('0');
  const [amountSealed, setAmountSealed] = useState<string>('0');

  const isReturnOFType = orderFulfillmentType === OrderFulfillmentType.Return;
  const packingUnit = containerType.packingUnit;

  return (
    <Fragment key={containerType.id}>
      <td className="align-middle">
        <ContainerTypeDisplay containerType={containerType} />
      </td>

      {isReturnOFType && !hideAmountCleanColumn && (
        <td className="align-middle">
          <SelectManualAmountsAmounts
            amount={amountSealed}
            setAmount={setAmountSealed}
            packingUnit={packingUnit}
          />
        </td>
      )}

      <td className="align-middle">
        <SelectManualAmountsAmounts
          amount={amount}
          setAmount={setAmount}
          packingUnit={packingUnit}
        />
      </td>

      <td className="align-middle">
        <button
          className="btn btn-primary float-end mt-2 mb-2"
          disabled={isDisabled}
          onClick={() =>
            setManualAmounts([
              ...manualAmounts,
              {
                container_type_id: containerType.id,
                container_type_name: containerType.name,
                container_type_image: containerType.imageUrl,
                amount: Number(amount),
                amount_sealed:
                  isReturnOFType && !hideAmountCleanColumn
                    ? Number(amountSealed)
                    : 0,
              },
            ])
          }
        >
          <FaPlus />
          {' ' + i18n.t('Common:add')}
        </button>
      </td>
    </Fragment>
  );
};

const SELECT_AMOUNT_OPTIONS = ['All', '5', '10', '25', '50', '100'];

type OrderFulfillmentAddContainerTypesProps = {
  isDisabled: boolean;
  containerTypes: ContainerType[];
  orderFulfillmentType: OrderFulfillmentType;
  manualAmounts: ContainerTypeAmount[];
  setManualAmounts: (newAmounts: ContainerTypeAmount[]) => void;
  hideAmountCleanColumn?: boolean;
};

const OrderFulfillmentAddContainerTypes = ({
  isDisabled,
  containerTypes,
  orderFulfillmentType,
  manualAmounts,
  setManualAmounts,
  hideAmountCleanColumn,
}: OrderFulfillmentAddContainerTypesProps) => {
  const [rowCount, setRowCount] = useState<number>(5);
  const [nameFilter, setNameFilter] = useState<string>('');
  const [classFilter, setClassFilter] = useState<string[]>(['event_portfolio']);

  const containerTypesWithoutManualAmount = containerTypes.filter(
    (ct) => !manualAmounts.some((ma) => ma.container_type_id === ct.id),
  );

  const classes = containerTypesWithoutManualAmount.reduce(
    (acc: string[], cur) => {
      const containerTypeClass = cur.class?.toLowerCase();

      if (!acc.includes(containerTypeClass)) {
        acc.push(containerTypeClass);
      }

      return acc;
    },
    [],
  );

  const filteredContainerTypes = containerTypesWithoutManualAmount
    .filter((ct) => {
      if (classFilter.length === 0) {
        return true;
      }

      return classFilter.some((classToFilter) => {
        return ct.class.toLowerCase().includes(classToFilter);
      });
    })
    .sort((a, b) => a.id - b.id);

  let fuseResult = filteredContainerTypes;

  if (nameFilter.length !== 0) {
    const fuse = new Fuse(filteredContainerTypes, {
      keys: ['name', 'group', 'class'],
    });

    fuseResult = fuse.search(nameFilter).map((result) => {
      return result.item;
    });
  }

  const hasMoreRowsThenRowCount =
    fuseResult.length > rowCount && rowCount !== -1;
  const hasClassFilter = classFilter.length > 0;

  const showResetButton = hasClassFilter;

  const isReturnOFType = orderFulfillmentType === OrderFulfillmentType.Return;
  const isDeliveryOFType =
    orderFulfillmentType === OrderFulfillmentType.Delivery;

  return (
    <>
      <div className="d-flex flex-wrap">
        <div className="input-group mb-3 flex-nowrap">
          <span className="input-group-text">Filter by Class</span>

          <MultiSelect
            className="form-control"
            value={classFilter}
            onChange={(value: string[]) => setClassFilter(value)}
            data={classes}
            searchable
            placeholder={i18n.t('Common:selectClasses')}
          />
        </div>

        <div className="input-group mb-3 flex-nowrap">
          <Input
            type="text"
            className="form-control"
            value={nameFilter}
            onChange={(event) => setNameFilter(event.currentTarget.value)}
            placeholder={i18n.t('Common:searchByName')}
          />
        </div>
      </div>

      <p>
        {`Showing ${
          hasMoreRowsThenRowCount ? rowCount : fuseResult.length || 0
        } of ${containerTypesWithoutManualAmount.length || 0} results${
          hasMoreRowsThenRowCount ? ` (limited to ${rowCount} rows)` : ''
        }`}{' '}
        {showResetButton && (
          <Button
            onClick={() => {
              setClassFilter([]);
            }}
          >
            Reset filter
          </Button>
        )}
      </p>

      <Table striped hover bordered>
        <thead>
          <tr>
            <th>{i18n.t('CreateOrderFulfillment:containerType')}</th>

            {isReturnOFType && !hideAmountCleanColumn && (
              <>
                <th>{i18n.t('Common:amountClean')}</th>
                <th>{i18n.t('Common:amountDirty')}</th>
              </>
            )}

            {isDeliveryOFType && <th>{i18n.t('Common:amountClean')}</th>}

            <th></th>
          </tr>
        </thead>
        <tbody>
          {fuseResult.map((containerType, index) => {
            if (rowCount > -1 && index >= rowCount) {
              return null;
            }

            return (
              <tr key={containerType.id}>
                <ContainerTypeRow
                  containerType={containerType}
                  orderFulfillmentType={orderFulfillmentType}
                  isDisabled={isDisabled}
                  manualAmounts={manualAmounts}
                  setManualAmounts={setManualAmounts}
                  hideAmountCleanColumn={hideAmountCleanColumn}
                />
              </tr>
            );
          })}
        </tbody>
      </Table>

      <div className="input-group mb-3">
        <span className="input-group-text">Row limit</span>
        <Select
          data={SELECT_AMOUNT_OPTIONS}
          value={
            rowCount === -1 ? SELECT_AMOUNT_OPTIONS.at(0) : rowCount.toString()
          }
          onChange={(value: string) => {
            if (value === SELECT_AMOUNT_OPTIONS.at(0)) {
              setRowCount(-1);
              return;
            }

            setRowCount(Number(value));
          }}
        ></Select>
      </div>
    </>
  );
};

export default OrderFulfillmentAddContainerTypes;
